import React from "react";
import { Box, Card, Typography, Chip } from "@mui/material";
import { COLORS, CircleCheckbox } from "./wtStyle";
import { dormancyDays, dormancyLabel } from "./dormancy";

// Site Visit is split into 3 owner-side sub-buckets by verification status
// (bucketComputation.js's bucketRows) — raw key names read awkwardly as plain
// text, so give them a friendlier label here.
export const BUCKET_LABELS = {
  SiteVisitVerified: "Site Visit — Verified",
  SiteVisitUnverified: "Site Visit — Unverified",
  SiteVisitRejected: "Site Visit — Rejected",
};

// Avatar color keyed by bucket, not per-lead — the color tells you which
// bucket a lead is in at a glance, same job the prototype's colored circles
// did (there it was semi-arbitrary; here it's meaningful).
const BUCKET_AVATAR_COLOR = {
  Nurturing: "#0891b2",
  SiteVisitVerified: "#2563eb",
  SiteVisitUnverified: "#2563eb",
  SiteVisitRejected: "#2563eb",
  Token: COLORS.orange,
  Allotment: COLORS.green,
};

function initialOf(name) {
  return (name || "?").trim().charAt(0).toUpperCase() || "?";
}

// One lead row — colored avatar, name + display id, one secondary line
// (project + dormancy, or bucket-specific detail). Matches the "Move Work
// Insights" prototype's always-visible row instead of the app's older
// click-to-expand LeadRow (LeadDetailsPanel.jsx) — kept separate rather than
// reusing LeadRow since the two are visually unrelated by design here.
function BucketLeadRow({ lead, bucketName, bare = false }) {
  const days = dormancyDays(lead.last_updated_at);
  const isToken = bucketName === "Token";
  const isAllotment = bucketName === "Allotment";

  const detail = isToken
    ? [lead.tokenname, lead.eventname].filter(Boolean).join(" · ")
    : isAllotment
    ? [lead.wingname && `Wing ${lead.wingname}`, lead.unitno && `Unit ${lead.unitno}`].filter(Boolean).join(", ")
    : null;

  // salesname was previously repeated on every row here, but the bucket
  // header above (BucketLeadGroups' distinctNamesLabel) already summarizes
  // it for the whole bucket — showing it again per row was redundant
  // whenever a bucket only spans one SM, which is the common case
  // (2026-08-25, removed after it read as noise in a screenshot).
  // leadstatus inline here too (2026-08-26) — was a fixed-width Chip that
  // truncated longer statuses ("Cancellation Confirmation Pending by X") to
  // an unreadable "...". Plain text wraps instead of clipping.
  const secondary = [lead.projectname || lead.projectshortcode || "—", detail || dormancyLabel(days), lead.leadstatus]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      sx={
        bare
          ? { display: "flex", alignItems: "center", gap: 1.5, py: 1 }
          : { display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5, borderTop: `1px solid ${COLORS.border}`, "&:hover": { bgcolor: COLORS.sunken } }
      }
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11.5,
          fontWeight: 700,
          color: "#fff",
          bgcolor: BUCKET_AVATAR_COLOR[bucketName] || COLORS.blue,
        }}
      >
        {initialOf(lead.leadname)}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontSize={13.5} fontWeight={700} noWrap>
          {lead.leadname || "Unnamed lead"}
          <Typography component="span" fontSize={11.5} fontWeight={500} color={COLORS.textSecondary} sx={{ ml: 0.75 }}>
            {lead.leaddisplayid || ""}
          </Typography>
        </Typography>
        <Typography fontSize={11.5} color={COLORS.textSecondary}>
          {secondary}
        </Typography>
      </Box>
    </Box>
  );
}

// One selectable lead row — same as BucketLeadRow's content, plus its own
// checkbox on the left so a lead can be individually left behind while the
// rest of its bucket still moves (2026-08-19 — needs the backend's
// excludedLeadIds support in transferFlows.js/collectSelectedLeads, since a
// whole-bucket-only execute call can't express "everyone but this one"). A
// checked row gets its own tint (2026-08-24) — a small checkbox alone was too
// easy to lose track of once a bucket's list scrolled past a few rows.
function SelectableLeadRow({ lead, bucketName, checked, onToggle }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        borderTop: `1px solid ${COLORS.border}`,
        bgcolor: checked ? COLORS.blueLight : "transparent",
        "&:hover": { bgcolor: checked ? COLORS.blueLight : COLORS.sunken },
      }}
    >
      <CircleCheckbox size="small" checked={checked} onChange={onToggle} sx={{ ml: 0.5 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <BucketLeadRow lead={lead} bucketName={bucketName} bare />
      </Box>
    </Box>
  );
}

// Move Someone's Work-specific bucket UI: leads render inline under their
// own bucket as soon as it's ticked, instead of BucketSelector's click-to-
// reveal side panel (LeadDetailsPanel) — matches CP Sourcing's always-visible
// list. Kept as its own component rather than changing BucketSelector, which
// is still used as-is by Transfer-by-project/Bulk-lead-source-change on the
// full reference branch. Header checkbox is 3-state (all/none/indeterminate,
// same pattern CpSourcingReassignPage uses) and drives every lead checkbox in
// the bucket; individual lead checkboxes can override it to exclude specific
// leads from an otherwise-selected bucket.
// Global lead search (2026-08-24) — a bucket's boundary is an implementation
// detail the admin often doesn't know or care about ("is Rahul's lead in
// Nurturing or Site Visit?"); this lets them type a name/id once and see
// which bucket(s) it's actually sitting in, instead of expanding each one to
// look. Filters which leads RENDER inside an already-selected/expanded
// bucket only — never touches selection state, so a search doesn't silently
// change what would be transferred.
function matchesSearch(lead, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (lead.leadname || "").toLowerCase().includes(q) || (lead.leaddisplayid || "").toLowerCase().includes(q);
}

// Lead type (CP/Direct/R&L) scopes the transfer the same way ticking a
// bucket does (2026-08-24) — unlike search, this isn't just a display aid:
// a non-matching lead is folded into excludedInBucket below exactly like a
// manually-unticked one, so "X of Y selected" and the header checkbox both
// reflect the real, type-scoped selection. MoveWorkPage.jsx independently
// does the same folding when building the actual execute payload.
function matchesLeadType(lead, leadTypeFilter) {
  return leadTypeFilter === "All" || lead.leadtype === leadTypeFilter;
}

// Header-level summary of WHO a bucket's leads currently belong to — self
// scope always resolves to one name, but "Self + team" can pool leads from
// several people into one bucket, so this is the at-a-glance answer to "is
// this bucket a single person's or a mix?" without opening every row
// (2026-08-25). Capped at 2 names + a "+N more" tail so a large team doesn't
// blow out the header's width.
export function distinctNamesLabel(items, field, prefix) {
  const names = [...new Set((items || []).map((item) => item[field]).filter(Boolean))];
  if (names.length === 0) return null;
  if (names.length <= 2) return `${prefix}: ${names.join(", ")}`;
  return `${prefix}: ${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
}

export default function BucketLeadGroups({
  buckets,
  selected,
  onToggle,
  excludedLeadIds = [],
  onToggleLead,
  onSelectAllInBucket,
  searchQuery = "",
  leadTypeFilter = "All",
  allotmentOptions,
}) {
  const names = Object.keys(buckets || {});
  if (names.length === 0) {
    return (
      <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 2 }}>
        No eligible leads found.
      </Typography>
    );
  }

  const excludedSet = new Set(excludedLeadIds);
  const isSearching = searchQuery.trim().length > 0;
  // Search hides an empty-match bucket entirely (it's a "find one thing"
  // tool); the type filter doesn't — every bucket always stays visible with
  // its real (possibly zero) count, same principle the backend already
  // applies to bucket.count itself.
  const visibleNames = isSearching
    ? names.filter((name) => buckets[name].leads.some((l) => matchesSearch(l, searchQuery) && matchesLeadType(l, leadTypeFilter)))
    : names;

  if (isSearching && visibleNames.length === 0) {
    return (
      <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 2 }}>
        No leads match "{searchQuery}" in any bucket.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {visibleNames.map((name) => {
        const bucket = buckets[name];
        const isSelected = selected.includes(name);
        const leadIds = bucket.leads.map((l) => l.id);
        const typeMismatchIds = new Set(bucket.leads.filter((l) => !matchesLeadType(l, leadTypeFilter)).map((l) => l.id));
        const excludedInBucket = leadIds.filter((id) => excludedSet.has(id) || typeMismatchIds.has(id));
        const includedCount = leadIds.length - excludedInBucket.length;
        // Header checked/indeterminate is judged only against leads the type
        // filter actually allows — a type-mismatched lead can never become
        // "included" no matter what's clicked, so counting it against
        // headerChecked meant a bucket with any wrong-type lead could never
        // reach fully-checked, leaving the header stuck on indeterminate and
        // every click routed to "select all" — there was no way to reach
        // "deselect this whole bucket" at all (2026-08-24 fix).
        const matchingLeadIds = leadIds.filter((id) => !typeMismatchIds.has(id));
        const manuallyExcludedMatching = matchingLeadIds.filter((id) => excludedSet.has(id));
        const headerChecked = isSelected && matchingLeadIds.length > 0 && manuallyExcludedMatching.length === 0;
        const headerIndeterminate = isSelected && manuallyExcludedMatching.length > 0 && manuallyExcludedMatching.length < matchingLeadIds.length;
        const isFiltering = isSearching || leadTypeFilter !== "All";
        const visibleLeads = bucket.leads.filter((l) => matchesSearch(l, searchQuery) && matchesLeadType(l, leadTypeFilter));
        const smLabel = distinctNamesLabel(bucket.leads, "salesname", "SM");

        const handleHeaderToggle = () => {
          if (!isSelected) {
            onSelectAllInBucket(name);
            onToggle(name);
          } else if (headerChecked || includedCount === 0) {
            onToggle(name);
          } else {
            onSelectAllInBucket(name);
          }
        };

        return (
          <Card
            key={name}
            elevation={0}
            sx={{
              border: `1px solid ${isSelected ? COLORS.blue : COLORS.border}`,
              borderRadius: 3,
              bgcolor: isSelected ? COLORS.blueLight : "#fff",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", p: 1.5, cursor: "pointer" }} onClick={handleHeaderToggle}>
              <CircleCheckbox
                checked={headerChecked}
                indeterminate={headerIndeterminate}
                onChange={handleHeaderToggle}
                onClick={(e) => e.stopPropagation()}
              />
              <Box sx={{ flex: 1 }}>
                <Typography fontSize={13.5} fontWeight={600}>{BUCKET_LABELS[name] || name}</Typography>
                {isSelected && excludedInBucket.length > 0 && (
                  <Typography fontSize={12} color={COLORS.textSecondary}>
                    {includedCount} of {leadIds.length} selected
                  </Typography>
                )}
                {bucket.excludedCount > 0 && (
                  <Typography fontSize={12} color={COLORS.textSecondary}>
                    {bucket.excludedReason}
                  </Typography>
                )}
              </Box>
              {smLabel && (
                <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mr: 1.5, flexShrink: 0 }} noWrap>
                  {smLabel}
                </Typography>
              )}
              <Chip
                label={isFiltering ? `${visibleLeads.length} match${visibleLeads.length === 1 ? "" : "es"}` : `${bucket.count} lead${bucket.count === 1 ? "" : "s"}`}
                size="small"
                sx={{ fontSize: 12, fontWeight: 500, bgcolor: COLORS.sunken, color: COLORS.textSecondary }}
              />
              {!isFiltering && bucket.excludedCount > 0 && (
                <Chip
                  label={`${bucket.excludedCount} excluded`}
                  size="small"
                  sx={{ ml: 1, fontSize: 12, fontWeight: 500, bgcolor: "#fef3c7", color: COLORS.orange }}
                />
              )}
            </Box>
            {/* Allotment-only, opt-in overrides for the two toggle-able
                exclusions (2026-08-25) — payment-conflicted leads have no
                toggle at all, that one stays hard-blocked. Only rendered
                when there's actually something to toggle (count > 0), and
                re-fetches immediately on change (see loadBuckets in
                MoveWorkPage.jsx), same as changing the date range. */}
            {name === "Allotment" && allotmentOptions && (bucket.crmTaggedCount > 0 || bucket.agreementDoneCount > 0) && (
              <Box sx={{ px: 1.5, pb: 1.25, display: "flex", flexDirection: "column", gap: 0.5, borderTop: `1px solid ${COLORS.border}` }}>
                {bucket.crmTaggedCount > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }} onClick={(e) => e.stopPropagation()}>
                    <CircleCheckbox
                      size="small"
                      checked={allotmentOptions.includeCrmTagged}
                      onChange={(e) => allotmentOptions.onToggleCrmTagged(e.target.checked)}
                    />
                    <Typography fontSize={12.5} color={COLORS.textSecondary}>
                      Include CRM-tagged leads ({bucket.crmTaggedCount})
                    </Typography>
                  </Box>
                )}
                {bucket.agreementDoneCount > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} onClick={(e) => e.stopPropagation()}>
                    <CircleCheckbox
                      size="small"
                      checked={allotmentOptions.includeAgreementDone}
                      onChange={(e) => allotmentOptions.onToggleAgreementDone(e.target.checked)}
                    />
                    <Typography fontSize={12.5} color={COLORS.textSecondary}>
                      Include Agreement Done leads ({bucket.agreementDoneCount})
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {visibleLeads.length > 0 && (
              <Box sx={{ bgcolor: "#fff", maxHeight: 280, overflowY: "auto" }}>
                {visibleLeads.map((lead) => (
                  <SelectableLeadRow
                    key={lead.id}
                    lead={lead}
                    bucketName={name}
                    checked={isSelected && !excludedSet.has(lead.id)}
                    onToggle={() => onToggleLead(name, lead.id)}
                  />
                ))}
              </Box>
            )}
          </Card>
        );
      })}
    </Box>
  );
}
